import { FC, useCallback } from 'react';
import papaparse from 'papaparse';
import { AllieFactoryGptResponseProps } from './FactoryGptResponse.types';
import { StyledDatagrid, DatagridWrapper } from './FactoryGptResponse.styles';
import { LoadServerRowsType } from '../../../components';
import { Chart } from './Chart';
import { AllieDownloadIcon } from '../../../styling/icons';
import { downloadBlobFile } from '../../../common/utils';
import { DownloadFormatMenu, exportXLSXFile } from '../ReportDownloader';

export const AllieFactoryGptResponse: FC<AllieFactoryGptResponseProps> = (props) => {
  const {
    type,
    title,
    data,
    disableAnimation = false,
    onDownloadCsv,
    downloadCsvTooltip,
    onDownloadXlsx,
  } = props;

  const handleDownloadCsv = useCallback(async () => {
    if (type !== 'csv') return;
    const filename = `factorygpt-data-${
      new Date().toISOString().split('T')[0]
    }.csv`;
    const blob = new Blob([data], { type: 'text/csv;charset=utf-8;' });
    await downloadBlobFile(blob, filename);
    onDownloadCsv?.(data);
  }, [data, type, onDownloadCsv]);

  if (type === 'csv') {
    const parseResult = papaparse.parse(data, {
      header: true,
      dynamicTyping: true,
    });

    const categories = parseResult.meta.fields || data.split(',').map(header => header.trim());
    const parsedData = parseResult.data as { [key: string]: number }[];

    const handleSelectFormat = async (format: 'csv' | 'xlsx') => {
      if (format === 'csv') {
        await handleDownloadCsv();
        return;
      }
      const headers = categories.reduce(
        (acc, category) => ({ ...acc, [category]: category }),
        {} as Record<string, string>
      );
      const filename = `factorygpt-data-${new Date().toISOString().split('T')[0]}`;
      await exportXLSXFile(headers, [...parsedData], filename);
      onDownloadXlsx?.(data);
    };

    const loadServerRows: LoadServerRowsType = () => {
      return new Promise((resolve) => {
        setTimeout(() => {
          resolve({
            data: parsedData,
            totalItems: parsedData.length,
          });
        }, 1);
      });
    };

    return (
      <DatagridWrapper>
        <StyledDatagrid
          columns={categories.map((category, index) => ({
            field: category,
            header: {
              title: categories[index] || '',
              label: category,
            },
          }))}
          loadServerRows={loadServerRows}
          {...{
            dataGridType: 'auto',
            title: title || '',
            showTitle: true,
            limit: 100,
          }}
        />
        <DownloadFormatMenu
          icon={<AllieDownloadIcon />}
          onSelect={handleSelectFormat}
          ariaLabel={downloadCsvTooltip || 'Download'}
        />
      </DatagridWrapper>
    );
  }

  return <Chart {...props} disableAnimation={disableAnimation} />;
};
